/**
 * RenderingCanvas - 珠宝渲染画布组件
 *
 * 封装了通用的 Canvas 配置，支持三种渲染技术路线的对比
 *
 * @component
 * @example
 * ```tsx
 * <RenderingCanvas mode="raymarching" />
 * <RenderingCanvas mode="pbr" />
 * <RenderingCanvas mode="raytracing" />
 * ```
 */

import { useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Stage, Stats } from "@react-three/drei";
import { RayMarchingScene } from "./RayMarchingScene";
import { PBRRenderingScene } from "./PBRRenderingScene";
import { RayTracingScene } from "./RayTracingScene";
import { PBRPostProcessing } from "./PBRPostProcessing";

interface RenderingCanvasProps {
  mode: "raymarching" | "pbr" | "raytracing";
  className?: string;
  resetTrigger?: number; // 用于触发相机重置
  showStats?: boolean; // 是否显示性能统计
  useEnvironmentMap?: boolean; // 是否启用环境贴图
  selectedHDR?: string; // 选择的HDR文件名
  environmentIntensity?: number; // 环境光强度
  environmentExposure?: number; // HDR曝光控制
  crystalMaterial?: {
    metalness: number; // 金属度 0.0-1.0
    roughness: number; // 粗糙度 0.0-1.0
    clearcoat: number; // 清漆层 0.0-1.0
    clearcoatRoughness: number; // 清漆粗糙度 0.0-1.0
    transmission: number; // 透射率 0.0-1.0
    opacity: number; // 不透明度 0.0-1.0
    ior: number; // 折射率 1.0-2.5
    dispersion: number; // 色散 0.0-5.0 (支持极端艺术化效果)
  }; // 水晶材质参数 - 仅PBR模式使用，支持UI实时调节
}

// 相机控制器组件，支持重置功能
function CameraController({
  mode,
  resetTrigger,
}: {
  mode: "raymarching" | "pbr" | "raytracing";
  resetTrigger?: number;
}) {
  const controlsRef = useRef<any>(null);
  const isRayMarchingMode = mode === "raymarching";

  useEffect(() => {
    if (controlsRef.current && resetTrigger !== undefined) {
      // 重置相机位置到默认位置
      const targetPosition = isRayMarchingMode ? [0, 0, 7] : [0, 0, 8]; // 🎯 差异化相机距离：Ray Marching=7(shader优化), PBR=8(材质细节)
      controlsRef.current.object.position.set(...targetPosition);
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
  }, [resetTrigger, isRayMarchingMode]);

  return (
    <OrbitControls
      ref={controlsRef}
      enablePan={true}
      enableZoom={true}
      enableRotate={true}
      minDistance={isRayMarchingMode ? 2 : 4} // 🎯 最小距离：Ray Marching=2(近场shader), PBR=4(避免几何体穿透)
      maxDistance={isRayMarchingMode ? 20 : 30} // 🎯 最大距离：Ray Marching=20(shader范围), PBR=30(环境光衰减)
    />
  );
}

export function RenderingCanvas({
  mode,
  className,
  resetTrigger,
  showStats = false,
  useEnvironmentMap = false,
  selectedHDR = "brown_photostudio_02_2k.hdr",
  environmentIntensity = 2.0,
  environmentExposure = 1.0, // HDR默认曝光调整为1.0
  crystalMaterial,
}: RenderingCanvasProps) {
  // 针对不同场景的配置
  const isRayMarchingMode = mode === "raymarching";

  return (
    <Canvas
      camera={{
        position: isRayMarchingMode ? [0, 0, 7] : [0, 0, 8], // 🎯 相机距离优化：基于各渲染技术的最佳观察距离设计
        fov: 50,
      }}
      gl={{ antialias: true, alpha: true }}
      className={className}
    >
      {/* 性能统计 - 显示在左上角 */}
      {showStats && <Stats />}

      {isRayMarchingMode ? (
        // RayMarchingScene 使用全屏shader技术，内置相机系统和环境光采样
        <>
          <RayMarchingScene
            useEnvironmentMap={useEnvironmentMap}
            selectedHDR={selectedHDR}
            environmentIntensity={environmentIntensity}
            environmentExposure={environmentExposure}
          />
          <CameraController mode={mode} resetTrigger={resetTrigger} />
        </>
      ) : mode === "pbr" ? (
        // PBRRenderingScene 使用MeshPhysicalMaterial + 多光源系统 + 后处理色调映射
        <>
          {/* 🎯 PBR多光源系统：手动光照配置避免Stage组件与ShaderMaterial冲突 */}
          <ambientLight intensity={useEnvironmentMap ? 0.4 : 0.8} />{" "}
          {/* 基础环境光：补偿HDR环境贴图不足 */}
          <directionalLight
            position={[5, 5, 5]}
            intensity={useEnvironmentMap ? 0.7 : 1.2}
            castShadow={false} // 禁用阴影以避免透明材质的渲染复杂度
          />
          <directionalLight
            position={[-3, 4, -2]}
            intensity={useEnvironmentMap ? 0.4 : 0.6}
            color="#FFF8DC" // 色温3200K暖白光：增强激光内雕点的暖色调特征
          />
          <pointLight
            position={[-5, 5, 5]}
            intensity={useEnvironmentMap ? 0.5 : 0.8}
            distance={20} // 物理衰减距离：模拟真实点光源的平方反比衰减
          />
          <pointLight
            position={[5, -3, -5]}
            intensity={useEnvironmentMap ? 0.3 : 0.5}
            color="#E6F3FF" // 色温6500K冷白光：与暖光形成色温对比，增强立体感
            distance={15}
          />
          <pointLight
            position={[0, -5, 0]}
            intensity={useEnvironmentMap ? 0.3 : 0.5}
            color="#4FC3F7" // 蓝色辅助光：增强水晶底部的光学层次
            distance={18}
          />
          <PBRRenderingScene
            useEnvironmentMap={useEnvironmentMap}
            selectedHDR={selectedHDR}
            environmentIntensity={environmentIntensity}
            environmentExposure={environmentExposure}
            crystalMaterial={crystalMaterial}
          />
          <CameraController mode={mode} resetTrigger={resetTrigger} />
          {/* 🎯 PBR后处理管线：ACES色调映射 + 渲染器物理光照配置 */}
          <PBRPostProcessing toneMappingExposure={1.0} enabled={true} />
        </>
      ) : (
        // RayTracingScene 使用全屏shader + 内置光线追踪，无需Three.js光源
        <>
          <RayTracingScene
            useEnvironmentMap={useEnvironmentMap}
            selectedHDR={selectedHDR}
            environmentIntensity={environmentIntensity}
            environmentExposure={environmentExposure}
          />
          <CameraController mode={mode} resetTrigger={resetTrigger} />
        </>
      )}
    </Canvas>
  );
}
